import {
  ProductPricingContext,
  PriceMap,
  ProductPricingContextShape,
} from '../productPricing/ProductPricingContext'
import React from 'react'
import { PriceTypeLabel } from '../list/PriceTypeLabel'
import { PackQuantityProvider } from '../bom/bomUtils'

/**
 * Generic hook for getting product prices from somewhere
 * Fetches pricing for provided items, and returns the current accumulated pricing data
 * @param itemNos item numbers to request pricing for
 * @param context
 */
export function useProductPricesWithContext(itemNos: readonly string[], context: ProductPricingContextShape): {
  priceMap: PriceMap
  isFetching: boolean
  priceTypeLabel: PriceTypeLabel
  getPackQuantity: PackQuantityProvider
  isEnabled: boolean
} {
  const { priceMap, requestPricing, isFetching, priceTypeLabel, getPackQuantity, isEnabled } = context

  // convert item numbers to a single string to avoid excessive effect calls
  const itemNoListString = itemNos.join(' ')

  React.useEffect(() => {
    const bomItemNos = itemNoListString.split(' ').filter(it => it.length > 0)
    if (bomItemNos.length === 0) {
      return
    }

    requestPricing(bomItemNos)
  }, [requestPricing, itemNoListString])

  return {
    priceMap,
    isFetching,
    priceTypeLabel,
    getPackQuantity,
    isEnabled,
  }
}

/**
 * Convenience wrapper for {@link useProductPricesWithContext}
 */
export function useProductPrices(itemNos: readonly string[]): {
  priceMap: PriceMap
  isFetching: boolean
  priceTypeLabel: PriceTypeLabel
  getPackQuantity: PackQuantityProvider
  isEnabled: boolean
} {
  return useProductPricesWithContext(itemNos, React.useContext(ProductPricingContext))
}